import type { B2BHome, B2BPo, B2BQuote, B2BServices } from '../b2bContracts';
import { api } from './client';

const B = '/api/b2b';
const paise = (rupees: number) => Math.round(Number(rupees) * 100);

type ApiQuote = { id: string; status: string; items: B2BQuote['items']; totals?: { total?: number }; messages?: B2BQuote['messages']; createdAt: string };
type ApiPo = { id: string; poNumber: string; status: string; items: unknown[]; totals?: { total?: number }; createdAt: string; orderId?: string | null };

function quoteView(q: ApiQuote): B2BQuote {
  return {
    id: q.id, status: q.status.toLowerCase(),
    items: (q.items ?? []).map((it) => ({ ...it, price: paise(Number(it.price ?? 0)), unitPrice: paise(Number(it.price ?? 0)), name: it.name })),
    totalAmount: paise(q.totals?.total ?? 0), createdAt: q.createdAt, messages: q.messages ?? [],
  };
}
function poView(p: ApiPo): B2BPo {
  return {
    id: p.id, poNumber: p.poNumber, status: p.status.toLowerCase(),
    totalAmount: paise(p.totals?.total ?? 0), itemCount: Array.isArray(p.items) ? p.items.length : 0,
    createdAt: p.createdAt, orderId: p.orderId,
  };
}

export const httpB2BServices: B2BServices = {
  async home(): Promise<B2BHome> {
    const me = await api<{
      company: { id: string; name: string; creditLimit: number; availableCredit: number; paymentTermsDays: number };
      quotes: ApiQuote[]; purchaseOrders: ApiPo[];
      requisitions: { id: string; name: string; status: string; items: unknown[]; createdAt: string }[];
    }>(`${B}/me`);
    return {
      company: {
        ...me.company,
        creditLimit: paise(me.company.creditLimit),
        availableCredit: paise(me.company.availableCredit),
      },
      quotes: (me.quotes ?? []).map(quoteView),
      purchaseOrders: (me.purchaseOrders ?? []).map(poView),
      requisitions: (me.requisitions ?? []).map((r) => ({
        id: r.id, name: r.name, status: r.status.toLowerCase(),
        itemCount: Array.isArray(r.items) ? r.items.length : 0, createdAt: r.createdAt,
      })),
    };
  },
  async catalog() {
    const page = await api<{ items: { id: string; name: string; sku: string; price: number; stock: number }[] }>(`${B}/catalog`);
    return (page.items ?? []).map((p) => ({ ...p, price: paise(p.price) }));
  },
  async createRequisition(name, items) {
    await api(`${B}/requisitions`, { method: 'POST', body: JSON.stringify({ name, items }) });
  },
  async createQuote(items) {
    await api(`${B}/quotes`, {
      method: 'POST',
      body: JSON.stringify({ items: items.map((it) => ({ ...it, price: Math.round(it.price / 100) })) }),
    });
  },
  async quote(id) {
    return quoteView(await api<ApiQuote>(`${B}/quotes/${id}`));
  },
  async quoteMessage(id, message) {
    await api(`${B}/quotes/${id}/messages`, { method: 'POST', body: JSON.stringify({ message }) });
  },
  async quoteAction(id, status) {
    await api(`${B}/quotes/${id}/action`, { method: 'POST', body: JSON.stringify({ status }) });
  },
  async createPo(items) {
    const row = await api<ApiPo>(`${B}/purchase-orders`, {
      method: 'POST',
      body: JSON.stringify({ items: items.map((it) => ({ ...it, price: Math.round(it.price / 100) })) }),
    });
    return poView(row);
  },
  async convertPo(id) {
    await api(`${B}/purchase-orders/${id}/convert`, { method: 'POST', body: '{}' });
  },
  async credit() {
    const row = await api<{ creditLimit: number; availableCredit: number; paymentTermsDays: number; transactions: { id: string; type: string; amount: number; balanceAfter: number; reference?: string; createdAt: string }[] }>(`${B}/credit`);
    return {
      ...row,
      creditLimit: paise(row.creditLimit),
      availableCredit: paise(row.availableCredit),
      transactions: (row.transactions ?? []).map((t) => ({ ...t, amount: paise(t.amount), balanceAfter: paise(t.balanceAfter) })),
    };
  },
  async inviteUser(input) {
    return api<{ temporaryPassword: string }>(`${B}/users`, { method: 'POST', body: JSON.stringify(input) });
  },
  async users() {
    const me = await api<{ company: { name: string } }>(`${B}/me`);
    const page = await api<{ items: { id: string; name: string; email: string; role: string; maxPoValue: number | null; status: string }[] }>(`${B}/users`);
    return {
      companyName: me.company.name,
      users: (page.items ?? []).map((u) => ({ ...u, maxPoValue: u.maxPoValue == null ? null : paise(u.maxPoValue), status: u.status.toLowerCase(), role: u.role.toLowerCase() })),
    };
  },
};
